import { NavigateOptions, PathParam, useNavigate, useSearchParams } from 'react-router-dom';
import { appGeneratePath } from '@/shared/lib/ts-router/utils/appGeneratePath';
import { RoutePath } from '../types';
import queryString from 'query-string';
import { createSafeNavigate } from '@/shared/lib';

/** Опции перехода: штатные опции роутера плюс работа с query-параметрами и фрагментом адреса */
export type AppNavigateOptions = NavigateOptions & {
  saveParams?: boolean;
  /** Дополнительные query-параметры, добавляются к URL поверх существующих */
  query?: Record<string, string | number | undefined | null>;
  /**
   * URL-фрагмент (`#hash`) целевого адреса. Принимается с ведущим `#` или без.
   * Подставляется как есть (уже закодированный источником) и повторно не кодируется.
   */
  hash?: string;
};

export const useAppNavigate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const safeNavigate = createSafeNavigate(navigate);

  return <Path extends RoutePath>(
    path: Path,
    params?: {
      [key in PathParam<Path>]: string | null;
    },
    options?: AppNavigateOptions,
  ) => {
    const resolvedPath = appGeneratePath(path, params);
    const { url, query: pathQuery, ...parts } = queryString.parseUrl(resolvedPath);

    // Фрагмент переносится отдельной строкой, минуя query-string: он приходит уже
    // закодированным, а stringifyUrl закодировал бы его повторно.
    const fragment = options?.hash ? options.hash.replace(/^#/, '') : '';
    const hashSuffix = fragment ? `#${fragment}` : '';

    if (!options?.saveParams && !options?.query && !hashSuffix) {
      safeNavigate(resolvedPath, options);
      return;
    }

    const extraQuery = options?.query
      ? Object.fromEntries(
          Object.entries(options.query)
            .filter(([, value]) => value != null && value !== '')
            .map(([key, value]) => [key, String(value)]),
        )
      : {};
    const savedParams = options?.saveParams ? Object.fromEntries(searchParams) : {};
    const nextParams = { ...pathQuery, ...savedParams, ...extraQuery };
    const nextPath = queryString.stringifyUrl({ url, query: nextParams, ...parts });
    safeNavigate(`${nextPath}${hashSuffix}`, options);
  };
};
